import { gql, useApolloClient, useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import {
  GET_ELD_APPS,
  GET_ELD_ASSET_LIST,
  type IELDApp,
  type IGetELDAppsResponse
} from '../../../../../../data/queries/eld-apps'
import { useAuth } from '../../../../../../hooks/use-auth'
import type { TDispatcher } from '../../../../../../types/shipment'
import { getCarrierWorkspaceIDFromDispatchers } from '../../../../../../utils/get-carrier-workspace-id-from-dispatchers'
import { cacheELDApps, clearELDAppsCache, getCachedELDApps } from './eld-apps-cache'
import type { TActiveAssetType } from './eld-types'

interface IUseELDAppsDataParams {
  activeAssetType: TActiveAssetType
  dispatchers?: TDispatcher[]
  isOpen: boolean
  selectedELDApp: IELDApp | null
  shipmentID: string
  shipmentOwnerWorkspaceID: string
  step: 1 | 2
}

/**
 * Encapsulates all ELD data-fetching logic: dispatcher caching, ELD apps query, and asset list query.
 */
export const useELDAppsData = ({
  activeAssetType,
  dispatchers,
  isOpen,
  selectedELDApp,
  shipmentID,
  shipmentOwnerWorkspaceID,
  step
}: IUseELDAppsDataParams) => {
  const { selectedWorkspace } = useAuth()
  const apolloClient = useApolloClient()
  const currentWorkspaceID = selectedWorkspace?.workspace.id || ''
  const [forceRefetch, setForceRefetch] = useState(false)

  /** Try to read dispatchers from Apollo cache if not provided */
  const cachedDispatchers = useMemo(() => {
    if (dispatchers && dispatchers.length > 0) {
      return dispatchers
    }

    try {
      const cachedShipment = apolloClient.cache.readFragment({
        id: `Shipment:${shipmentID}`,
        fragment: gql`
          fragment ShipmentDispatchers on Shipment {
            dispatchers {
              workspace {
                id
              }
            }
          }
        `
      })

      return (cachedShipment as { dispatchers?: TDispatcher[] })?.dispatchers
    } catch {
      return undefined
    }
  }, [dispatchers, apolloClient, shipmentID])

  const carrierWorkspaceID = getCarrierWorkspaceIDFromDispatchers(cachedDispatchers)

  const cacheKey = {
    carrierWorkspaceID: carrierWorkspaceID ?? null,
    currentWorkspaceID,
    shipmentOwnerWorkspaceID
  }

  const cachedELDApps = isOpen && !forceRefetch ? getCachedELDApps(cacheKey) : null

  const queryVariables = {
    carrierWorkspaceID: carrierWorkspaceID ?? null,
    currentWorkspaceID,
    shipmentOwnerWorkspaceID
  }

  const { data: eldAppsData, loading: loadingELDApps } = useQuery<IGetELDAppsResponse>(GET_ELD_APPS, {
    skip: !isOpen || !!cachedELDApps,
    fetchPolicy: 'network-only',
    variables: queryVariables,
    onError: (error: Error) => {
      console.error('Error fetching ELD apps:', error)
      toast.error('Failed to load ELD apps')
    },
    onCompleted: (data: IGetELDAppsResponse) => {
      if (data?.getELDApps) {
        cacheELDApps(cacheKey, data.getELDApps)
      }
      if (forceRefetch) {
        setForceRefetch(false)
      }
    }
  })

  const eldApps = cachedELDApps || eldAppsData?.getELDApps || []

  const refetchELDApps = () => {
    clearELDAppsCache()
    setForceRefetch(true)
  }

  const shouldFetchAssets = isOpen && step === 2 && !!selectedELDApp

  const { data: assetListData, loading: loadingAssets } = useQuery(GET_ELD_ASSET_LIST, {
    skip: !shouldFetchAssets,
    fetchPolicy: 'network-only',
    variables: shouldFetchAssets
      ? {
          appType: selectedELDApp!.appType,
          assetType: activeAssetType === 'trailer' ? 'Trailer' : 'Truck',
          workspaceID: selectedELDApp!.workspaceID
        }
      : undefined,
    onError: (error: Error) => {
      console.error('Error fetching asset list:', error)
    }
  })

  const assets = assetListData?.getELDAssetList || []

  return {
    assets,
    eldApps,
    loadingAssets,
    loadingELDApps,
    refetchELDApps
  }
}
